import React, { useEffect, useState } from 'react';
import SearchBar from '../components/SearchBar';
import RecordCard from '../components/RecordCard';
import RecordDetail from '../components/RecordDetail';
import EmptyState from '../components/EmptyState';
import ActivityLogModal from '../components/dashboard/ActivityLogModal';
import FilterChips from '../components/dashboard/FilterChips';
import { permissions as permissionsApi, records as recordsApi, audit as auditApi, apiFetch } from '../utils/api';
import { trackUxEvent } from '../utils/telemetry';

/** Purpose: Executes { records, permissions, on delete record, on grant permission, on view record, action signal }. Inputs: { records, permissions, onDeleteRecord, onGrantPermission, onViewRecord, actionSignal }. Output: Operation result. */
export default function Dashboard({ records, permissions, onDeleteRecord, onEditRecord, onGrantPermission, onViewRecord, actionSignal }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterProvider, setFilterProvider] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedRecordIds, setSelectedRecordIds] = useState([]);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [activityRecord, setActivityRecord] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState('');
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendStep, setSendStep] = useState('recipient');
  const [sendMode, setSendMode] = useState('email');
  const [sendEmail, setSendEmail] = useState('');
  const [selectedPermissionId, setSelectedPermissionId] = useState('');
  const [deliveryEmail, setDeliveryEmail] = useState('');
  const [requirePin, setRequirePin] = useState(true);
  const [pin, setPin] = useState('');
  const [sendError, setSendError] = useState('');
  const [sendSuccess, setSendSuccess] = useState(null);
  const [copiedSendLink, setCopiedSendLink] = useState(false);
  const [sending, setSending] = useState(false);
  const [modalRecordIds, setModalRecordIds] = useState([]);
  const filteredRecords = records.filter(record => {
    const matchesSearch = record.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         record.provider.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         record.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         record.type.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterType === 'all' || record.type === filterType;
    const matchesProvider = filterProvider === 'all' || record.provider === filterProvider;

    const recordDate = record.date ? new Date(record.date) : null;
    const fromDate = dateFrom ? new Date(dateFrom) : null;
    const toDate = dateTo ? new Date(`${dateTo}T23:59:59`) : null;
    const matchesFrom = !fromDate || (recordDate && recordDate >= fromDate);
    const matchesTo = !toDate || (recordDate && recordDate <= toDate);

    return matchesSearch && matchesFilter && matchesProvider && matchesFrom && matchesTo;
  });

  const activePermissions = permissions.filter(p => p.status === 'active');
  const recordTypes = [...new Set(records.map(r => r.type))];
  const providers = [...new Set(records.map(r => r.provider).filter(Boolean))].sort();
  const quickTypeChips = recordTypes.slice(0, 6);
  const quickProviderChips = providers.slice(0, 4);
  const filteredRecordIds = filteredRecords.map(r => r.id);
  const selectedCount = selectedRecordIds.length;
  const allVisibleSelected = filteredRecordIds.length > 0 && filteredRecordIds.every(id => selectedRecordIds.includes(id));
  /** Purpose: Handles view record. Inputs: record. Output: Operation result. */
  const handleViewRecord = (record) => {
    setViewingRecord(record);
    if (onViewRecord) {
      onViewRecord(record.id);
    }
  };

  /** Purpose: Fetches and shows record-specific activity log. Inputs: record. Output: Promise with operation result. */
  const handleViewRecordAudit = async (record) => {
    setActivityRecord(record);
    setActivityLogs([]);
    setActivityError('');
    setActivityLoading(true);
    try {
      const response = await auditApi.resourceHistory('record', record.id);
      const logs = Array.isArray(response?.data) ? response.data : [];
      setActivityLogs(logs);
    } catch (error) {
      console.error('Failed to load record activity:', error);
      setActivityError(error.message || 'Failed to load record activity');
    } finally {
      setActivityLoading(false);
    }
  };

  /** Purpose: Closes record activity modal. Inputs: none. Output: Operation result. */
  const handleCloseRecordAudit = () => {
    setActivityRecord(null);
    setActivityLogs([]);
    setActivityError('');
    setActivityLoading(false);
  };

  /** Purpose: Handles direct download from record tile. Inputs: record. Output: Promise with operation result. */
  const handleDownloadRecord = async (record) => {
    try {
      const response = await recordsApi.download(record.id);
      downloadBlobFile(response.blob, response.fileName || `${record.title}.pdf`);
    } catch (error) {
      console.error('Failed to download record:', error);
      window.alert('Failed to download record');
    }
  };

  // Track record view for audit
  /** Purpose: Handles record expand. Inputs: record, isExpanded. Output: Operation result. */
  const handleRecordExpand = (record, isExpanded) => {
    if (isExpanded && onViewRecord) {
      onViewRecord(record.id);
    }
  };

  /** Purpose: Handles toggle record selection. Inputs: recordId. Output: Operation result. */
  const handleToggleRecordSelection = (recordId) => {
    setSelectedRecordIds(prev =>
      prev.includes(recordId) ? prev.filter(id => id !== recordId) : [...prev, recordId]
    );
  };

  /** Purpose: Handles toggle select all visible. Inputs: none. Output: Operation result. */
  const handleToggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedRecordIds(prev => prev.filter(id => !filteredRecordIds.includes(id)));
      return;
    }
    setSelectedRecordIds(prev => [...new Set([...prev, ...filteredRecordIds])]);
  };

  /** Purpose: Handles clear selection. Inputs: none. Output: Operation result. */
  const handleClearSelection = () => {
    setSelectedRecordIds([]);
  };

  /** Purpose: Handles reset all. Inputs: none. Output: Operation result. */
  const handleResetAll = () => {
    setSearchQuery('');
    setFilterType('all');
    setFilterProvider('all');
    setDateFrom('');
    setDateTo('');
    setSelectedRecordIds([]);
  };

  /** Purpose: Fetches records to send. Inputs: none. Output: Operation result. */
  const getRecordsToSend = () => {
    if (modalRecordIds.length > 0) return modalRecordIds;
    return selectedRecordIds.length > 0 ? selectedRecordIds : filteredRecordIds;
  };

  /** Purpose: Executes open send modal. Inputs: recordId. Output: Operation result. */
  const openSendModal = (recordId) => {
    setModalRecordIds(recordId ? [recordId] : []);
    setShowSendModal(true);
    setSendStep('recipient');
    setSendError('');
    setSendSuccess(null);
    setCopiedSendLink(false);
    setSending(false);
  };

  /** Purpose: Executes close send modal. Inputs: none. Output: Operation result. */
  const closeSendModal = () => {
    setShowSendModal(false);
    setSendStep('recipient');
    setSendError('');
    setSendSuccess(null);
    setCopiedSendLink(false);
  };

  useEffect(() => {
    if (actionSignal?.type === 'send-records' && actionSignal?.token) {
      openSendModal(null);
    }
  }, [actionSignal]);

  /** Purpose: Executes send records. Inputs: none. Output: Promise with operation result. */
  const sendRecords = async () => {
    const recordsToSend = getRecordsToSend();
    if (recordsToSend.length === 0) {
      setSendError('Select at least one record to send.');
      return;
    }
    if (sendMode === 'email' && !sendEmail.trim()) {
      setSendError('Recipient email is required.');
      return;
    }
    if (sendMode === 'permission' && !selectedPermissionId) {
      setSendError('Select a permissioned provider.');
      return;
    }
    if (requirePin && pin.length < 4) {
      setSendError('PIN must be at least 4 digits.');
      return;
    }

    setSending(true);
    setSendError('');
    try {
      let permissionId = '';
      let emailSent = false;

      if (sendMode === 'permission') {
        const existing = activePermissions.find(p => p.id === selectedPermissionId);
        if (!existing) throw new Error('Selected permission not found');

        const existingIds = (existing.recordIds || []).slice().sort().join(',');
        const nextIds = recordsToSend.slice().sort().join(',');
        if (existingIds === nextIds) {
          permissionId = existing.id;
        } else {
          const created = await permissionsApi.create({
            grantedTo: existing.grantedTo,
            organization: existing.organization,
            purpose: `Secure record share (${recordsToSend.length} record${recordsToSend.length > 1 ? 's' : ''})`,
            expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            accessLevel: 'READ',
            recordIds: recordsToSend
          });
          permissionId = created.id;
        }
      } else {
        const created = await permissionsApi.create({
          grantedTo: sendEmail.trim(),
          organization: 'Email Recipient',
          purpose: `Secure record share (${recordsToSend.length} record${recordsToSend.length > 1 ? 's' : ''})`,
          expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          accessLevel: 'READ',
          recordIds: recordsToSend
        });
        permissionId = created.id;
      }

      const response = await apiFetch(`/share/generate/${permissionId}`, {
        method: 'POST',
        body: JSON.stringify({ pin: requirePin ? pin : null })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate secure link');
      }

      const recipient = sendMode === 'email' ? sendEmail.trim() : deliveryEmail.trim();
      const link = data.shareUrl;
      if (recipient) {
        const sendResponse = await apiFetch(`/share/send/${permissionId}`, {
          method: 'POST',
          body: JSON.stringify({ to: recipient, pin: requirePin ? pin : null })
        });
        const sendData = await sendResponse.json();
        if (!sendResponse.ok) {
          throw new Error(sendData.error || 'Failed to send email');
        }
        emailSent = true;
      }

      setSendSuccess({ link, pin: requirePin ? pin : '', recipient, emailSent });
      trackUxEvent('send_completion', {
        mode: sendMode,
        recordCount: recordsToSend.length,
        hasPin: requirePin,
        recipientType: sendMode === 'email' ? 'email' : 'permissioned_provider',
        emailSent
      });
      if (onGrantPermission) {
        onGrantPermission(null, true);
      }
    } catch (err) {
      console.error('Send records failed:', err);
      if (String(err.message || '').includes('Failed to generate secure link')) {
        setSendError('Could not generate a secure link. Confirm the recipient info and try again.');
      } else if (String(err.message || '').includes('Selected permission not found')) {
        setSendError('The selected provider permission is no longer active. Pick another provider and retry.');
      } else {
        setSendError(`${err.message || 'Failed to send records.'} You can review settings and retry.`);
      }
    } finally {
      setSending(false);
    }
  };

  /** Purpose: Handles copy send link. Inputs: none. Output: Promise with operation result. */
  const handleCopySendLink = async () => {
    if (!sendSuccess?.link) return;
    try {
      await navigator.clipboard.writeText(sendSuccess.link);
      setCopiedSendLink(true);
      setTimeout(() => setCopiedSendLink(false), 2000);
    } catch (error) {
      setSendError('Failed to copy link. You can copy it manually from the field.');
    }
  };

  /** Purpose: Executes next send step. Inputs: none. Output: Operation result. */
  const nextSendStep = () => {
    setSendError('');
    if (sendStep === 'recipient') {
      if (sendMode === 'email' && !sendEmail.trim()) {
        setSendError('Recipient email is required.');
        return;
      }
      if (sendMode === 'permission' && !selectedPermissionId) {
        setSendError('Select a permissioned provider.');
        return;
      }
      setSendStep('options');
      return;
    }

    if (sendStep === 'options') {
      if (requirePin && pin.length < 4) {
        setSendError('PIN must be at least 4 digits.');
        return;
      }
      setSendStep('confirmation');
    }
  };

  /** Purpose: Executes prev send step. Inputs: none. Output: Operation result. */
  const prevSendStep = () => {
    setSendError('');
    if (sendStep === 'confirmation') {
      setSendStep('options');
      return;
    }
    if (sendStep === 'options') {
      setSendStep('recipient');
    }
  };

  /** Purpose: Fetches filename from content disposition. Inputs: contentDisposition. Output: string | null. */
  const getFilenameFromDisposition = (contentDisposition) => {
    if (!contentDisposition) return null;
    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      try {
        return decodeURIComponent(utf8Match[1]);
      } catch (_error) {
        return utf8Match[1];
      }
    }
    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
    return filenameMatch?.[1] || null;
  };

  /** Purpose: Executes download blob file. Inputs: blob, fileName. Output: Operation result. */
  const downloadBlobFile = (blob, fileName) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  /** Purpose: Executes bulk download records. Inputs: none. Output: Promise with operation result. */
  const handleBulkDownload = async () => {
    const recordIds = selectedRecordIds.length > 0 ? selectedRecordIds : filteredRecordIds;
    if (recordIds.length === 0) return;

    let failureCount = 0;

    for (const recordId of recordIds) {
      const record = records.find((item) => item.id === recordId);
      try {
        const response = await apiFetch(`/records/${recordId}/download`);
        if (!response.ok) {
          throw new Error('Download failed');
        }
        const blob = await response.blob();
        const disposition = response.headers.get('content-disposition');
        const dispositionName = getFilenameFromDisposition(disposition);
        const fallbackName = (record?.fileName || record?.title || `record-${recordId}`).replace(/[^\w.-]+/g, '_');
        downloadBlobFile(blob, dispositionName || fallbackName);
      } catch (error) {
        console.error(`Failed to download record ${recordId}:`, error);
        failureCount += 1;
      }
    }

    if (failureCount > 0) {
      window.alert(`Downloaded ${recordIds.length - failureCount} of ${recordIds.length} records. ${failureCount} failed.`);
    }
  };


  return (
    <div className="space-y-6 animate-fade-in">
      <div className="bg-white rounded-2xl border border-slate-200/60 p-4 sm:p-6 shadow-card">
        <h2 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight">Records</h2>
        <p className="text-sm text-slate-400 mt-1">Search, filter, send, and manage your records.</p>
      </div>

        <SearchBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          filterType={filterType}
          setFilterType={setFilterType}
          filterProvider={filterProvider}
          setFilterProvider={setFilterProvider}
          dateFrom={dateFrom}
          setDateFrom={setDateFrom}
          dateTo={dateTo}
          setDateTo={setDateTo}
          recordTypes={recordTypes}
          providers={providers}
          onSendRecordsClick={() => openSendModal(null)}
          bulkCount={filteredRecords.length}
          selectedCount={selectedCount}
          allVisibleSelected={allVisibleSelected}
          onToggleSelectAllVisible={handleToggleSelectAllVisible}
          onBulkDownload={handleBulkDownload}
          onClearSelection={handleClearSelection}
          onClearFilters={handleResetAll}
        />

      {records.length > 0 && (
        <FilterChips
          filterType={filterType}
          setFilterType={setFilterType}
          filterProvider={filterProvider}
          setFilterProvider={setFilterProvider}
          quickTypeChips={quickTypeChips}
          quickProviderChips={quickProviderChips}
          onResetAll={handleResetAll}
        />
      )}

      <div className="space-y-3">
      {filteredRecords.length === 0 ? (
        <EmptyState
          icon="records"
          title={searchQuery || filterType !== 'all' ? "No matching records" : "No records yet"}
          description={
            searchQuery || filterType !== 'all'
              ? "Try adjusting your search or filter"
              : "Import your first record or request records from a provider"
          }
          actions={
            searchQuery || filterType !== 'all'
              ? [
                  { label: 'Clear filters', onClick: () => { setSearchQuery(''); setFilterType('all'); } }
                ]
              : [
                  { label: 'Import', onClick: () => window.location.href = '/?view=import', primary: true },
                  { label: 'Request From Provider', onClick: () => window.location.href = '/?view=import&section=request' }
                ]
          }
        />
	      ) : (
	          filteredRecords.map(record => (
	            <RecordCard
	              key={record.id}
	              record={record}
              selected={selectedRecordIds.includes(record.id)}
              onToggleSelect={() => handleToggleRecordSelection(record.id)}
              onDelete={onDeleteRecord}
              onEdit={onEditRecord}
              onView={handleViewRecord}
              onDownload={handleDownloadRecord}
              onViewAudit={handleViewRecordAudit}
              onSend={() => openSendModal(record.id)}
              onExpand={(isExpanded) => handleRecordExpand(record, isExpanded)}
            />
	          ))
	        )}
	      </div>

	      {showSendModal && (
	        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-slate-900/60 p-3 sm:p-4 overflow-y-auto">
	          <div className="w-full max-w-2xl rounded-2xl bg-white border border-slate-200 shadow-2xl p-4 sm:p-6 my-4 sm:my-0 max-h-[calc(100vh-2rem)] overflow-y-auto">
	            <div className="mb-5">
	              <h3 className="text-lg font-bold text-slate-900">Send records</h3>
	              <p className="mt-1 text-sm text-slate-500">Create a secure share link for selected records.</p>
	            </div>

	            {sendStep === 'recipient' && (
	              <div className="space-y-4">
	                <div className="flex flex-col sm:flex-row gap-2">
	                  <button
                    onClick={() => setSendMode('email')}
                    className={`w-full sm:w-auto px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${sendMode === 'email' ? 'bg-brand-600 text-white shadow-sm' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                  >
                    Email Recipient
                  </button>
                  <button
                    onClick={() => setSendMode('permission')}
                    className={`w-full sm:w-auto px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${sendMode === 'permission' ? 'bg-brand-600 text-white shadow-sm' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                  >
                    Permissioned Provider
                  </button>
                </div>

                {sendMode === 'email' ? (
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Recipient Email</label>
                    <input
                      type="email"
                      value={sendEmail}
                      onChange={(e) => setSendEmail(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm"
                      placeholder="recipient@example.com"
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Select Provider</label>
                      <select
                        value={selectedPermissionId}
                        onChange={(e) => setSelectedPermissionId(e.target.value)}
                        className="w-full px-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm"
                      >
                        <option value="">Choose existing permission...</option>
                        {activePermissions.map(permission => (
                          <option key={permission.id} value={permission.id}>
                            {permission.grantedTo} - {permission.organization}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Optional Delivery Email</label>
                      <input
                        type="email"
                        value={deliveryEmail}
                        onChange={(e) => setDeliveryEmail(e.target.value)}
                        className="w-full px-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm"
                        placeholder="provider@example.com"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {sendStep === 'options' && (
              <div className="space-y-4">
                <label className="flex items-center gap-3 text-sm text-slate-600 bg-slate-50 p-3.5 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors">
                  <input
                    type="checkbox"
                    checked={requirePin}
                    onChange={(e) => setRequirePin(e.target.checked)}
                    className="rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                  <span className="font-medium">Require PIN for access</span>
                </label>
                {requirePin && (
                  <input
                    type="text"
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full px-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm"
                    placeholder="4-6 digit PIN"
                  />
                )}
              </div>
            )}

            {sendStep === 'confirmation' && (
              <div className="space-y-3 text-sm">
                <div className="rounded-2xl border border-slate-100 bg-slate-50/50 p-4 space-y-2">
                  <p><span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Recipient:</span> <span className="text-slate-700 font-medium ml-1">{sendMode === 'email' ? sendEmail.trim() : (activePermissions.find((p) => p.id === selectedPermissionId)?.grantedTo || 'Selected provider')}</span></p>
                  {sendMode === 'permission' && deliveryEmail.trim() && (
                    <p><span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Delivery Email:</span> <span className="text-slate-700 font-medium ml-1">{deliveryEmail.trim()}</span></p>
                  )}
                  <p><span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Records:</span> <span className="text-slate-700 font-medium ml-1">{getRecordsToSend().length}</span></p>
                  <p><span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">PIN:</span> <span className="text-slate-700 font-medium ml-1">{requirePin ? 'Required' : 'Not required'}</span></p>
                </div>
              </div>
            )}

            {sendError && <p className="text-sm text-red-600 font-medium">{sendError}</p>}
            {sendSuccess && (
              <div className="p-4 bg-emerald-50/80 border border-emerald-200/60 rounded-2xl text-sm space-y-3">
                <p className="text-emerald-700 font-semibold">Secure link generated</p>
                {sendSuccess.emailSent && sendSuccess.recipient && (
                  <p className="text-emerald-700">Email sent to {sendSuccess.recipient}</p>
                )}
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    readOnly
                    value={sendSuccess.link}
                    className="w-full px-3 py-2.5 bg-white border border-emerald-200 rounded-xl text-xs font-mono"
                  />
                  <button
                    onClick={handleCopySendLink}
                    className="px-4 py-2.5 bg-emerald-600 text-white rounded-xl text-xs font-medium hover:bg-emerald-700 transition-colors whitespace-nowrap"
                  >
                    Copy
                  </button>
                </div>
                {sendSuccess.pin && <p className="text-emerald-700 font-medium">PIN: {sendSuccess.pin}</p>}
                <p className="text-xs text-emerald-600">
                  {sendSuccess.emailSent
                    ? 'Email sent from MedCaddy. Share the PIN through a separate channel if required.'
                    : 'Share the link with the recipient and send the PIN through a separate channel.'}
                </p>
                {copiedSendLink && <p className="text-xs text-emerald-700 font-medium">Link copied.</p>}
              </div>
            )}

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
              <button
                onClick={closeSendModal}
                className="w-full sm:w-auto px-4 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 transition-all"
              >
                Close
              </button>
              {sendStep !== 'recipient' && (
                <button
                  onClick={prevSendStep}
                  className="w-full sm:w-auto px-4 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 transition-all"
                >
                  Back
                </button>
              )}
              {sendStep !== 'confirmation' ? (
                <button
                  onClick={nextSendStep}
                  className="w-full sm:w-auto px-5 py-2.5 bg-gradient-to-r from-brand-600 to-violet-600 text-white rounded-xl text-sm font-medium hover:shadow-glow-blue transition-all"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={sendRecords}
                  disabled={sending}
                  className="w-full sm:w-auto px-5 py-2.5 bg-gradient-to-r from-brand-600 to-violet-600 text-white rounded-xl text-sm font-medium hover:shadow-glow-blue disabled:opacity-50 transition-all"
                >
                  {sending ? 'Sending...' : 'Send Documents'}
	                </button>
	              )}
	            </div>
	          </div>
	        </div>
	      )}

	      {viewingRecord && (
	        <RecordDetail
          record={viewingRecord}
          onClose={() => setViewingRecord(null)}
        />
      )}

      <ActivityLogModal
        record={activityRecord}
        logs={activityLogs}
        loading={activityLoading}
        error={activityError}
        onClose={handleCloseRecordAudit}
      />

      {selectedCount > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[min(760px,calc(100%-2rem))]">
          <div className="bg-slate-900 text-white rounded-xl shadow-2xl px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm">
              {selectedCount} record{selectedCount !== 1 ? 's' : ''} selected
            </p>
            <div className="flex w-full sm:w-auto flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <button
                onClick={handleClearSelection}
                className="w-full sm:w-auto px-3 py-1.5 rounded-lg border border-slate-500 text-slate-100 hover:bg-slate-800 text-sm"
              >
                Clear
              </button>
              <button
                onClick={() => openSendModal(null)}
                className="w-full sm:w-auto px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium"
              >
                Send Selected
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
